/**
 * Complete asset loading utility that handles both fonts and images
 * Combines functionality from waitForFonts.ts with image loading
 */

// const DEFAULT_MAX_WAIT_TIME = 5000;
const DEFAULT_MAX_WAIT_TIME = 13000;

interface ImageLoadTracker {
  element: Element;
  src: string;
  type: 'img' | 'background';
}

/**
 * Font loading functionality
 */
async function waitForFonts(maxWaitTime: number = DEFAULT_MAX_WAIT_TIME): Promise<void> {
  // // Find all the fonts in the DOM that we'd want to wait for.
  const allPreferredFonts = Array.from(document.querySelectorAll('*')).map(
    (element) => getComputedStyle(element).fontFamily.split(',')[0],
  );

  // // These are the fonts that are still to be loaded.
  const fontsToBeLoaded = Array.from(document.fonts).filter(
    (fontFace) =>
      allPreferredFonts.includes(fontFace.family) &&
      fontFace.status !== 'loaded' &&
      fontFace.status !== 'error',
  );

  if (fontsToBeLoaded.length > 0) {
    // // Relying on the browser's implicit font-loading (and the
    // // `loadingdone` event) only works if the browser has already decided
    // // to fetch these fonts. Chrome's heuristic for that is "does anything
    // // currently need to *paint* with this font" — elements that are still
    // // `opacity: 0` (e.g. a spinner/intro that fades in) don't count, so
    // // `loadingdone` never fires and this always fell through to the full
    // // maxWaitTime timeout. Explicitly calling `FontFace.load()` forces the
    // // fetch regardless of paint visibility and resolves as soon as the
    // // font data actually arrives.
    const loadPromise = Promise.all(
      fontsToBeLoaded.map((fontFace) => fontFace.load().catch(() => fontFace)),
    ).then(() => undefined);

    // // But we want to still resolve the promise if loading takes too long.
    // // Swapping fonts is still better than not showing the overlay at all.
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(resolve, maxWaitTime);
    });

    await Promise.race([loadPromise, timeoutPromise]);
  }
}

/**
 * Image loading functionality with MutationObserver
 */
async function waitForImages(
  maxWaitTime: number = DEFAULT_MAX_WAIT_TIME,
  rootElement: Element = document.body,
): Promise<void> {
  const pendingImages = new Set<ImageLoadTracker>();
  const abortController = new AbortController();

  // // Helper to parse CSS background-image URLs
  function parseBackgroundImageUrls(backgroundImage: string): string[] {
    const urlRegex = /url\(['"]?([^'")\s]+)['"]?\)/g;
    const urls: string[] = [];
    let match;

    while ((match = urlRegex.exec(backgroundImage)) !== null) {
      urls.push(match[1]);
    }

    return urls;
  }

  // // Helper to check if image is already loaded
  function isImageLoaded(img: HTMLImageElement): boolean {
    return img.complete && img.naturalWidth > 0;
  }

  // // Helper to add image tracking
  function trackImage(element: Element, src: string, type: 'img' | 'background') {
    const tracker: ImageLoadTracker = { element, src, type };
    pendingImages.add(tracker);

    const img = new Image();

    const cleanup = () => {
      pendingImages.delete(tracker);
      checkIfComplete();
    };

    img.addEventListener('load', cleanup, { once: true, signal: abortController.signal });
    img.addEventListener('error', cleanup, { once: true, signal: abortController.signal });

    img.src = src;
  }

  // // Helper to process a single element
  function processElement(element: Element) {
    // // Check if it's an img element
    if (element.tagName === 'IMG') {
      const img = element as HTMLImageElement;
      if (img.src && !isImageLoaded(img)) {
        trackImage(element, img.src, 'img');
      }
    }

    // // Check for CSS background images
    const computedStyle = getComputedStyle(element);
    if (computedStyle.backgroundImage && computedStyle.backgroundImage !== 'none') {
      const urls = parseBackgroundImageUrls(computedStyle.backgroundImage);
      urls.forEach((url) => {
        if (url && !url.startsWith('data:')) {
          trackImage(element, url, 'background');
        }
      });
    }

    // // Check child img elements
    const childImages = element.querySelectorAll('img');
    childImages.forEach((img) => {
      if (img.src && !isImageLoaded(img)) {
        trackImage(img, img.src, 'img');
      }
    });

    // // Check child elements with background images
    const allChildren = element.querySelectorAll('*');
    allChildren.forEach((child) => {
      const childStyle = getComputedStyle(child);
      if (childStyle.backgroundImage && childStyle.backgroundImage !== 'none') {
        const urls = parseBackgroundImageUrls(childStyle.backgroundImage);
        urls.forEach((url) => {
          if (url && !url.startsWith('data:')) {
            trackImage(child, url, 'background');
          }
        });
      }
    });
  }

  let resolvePromise: () => void;

  const imageLoadPromise = new Promise<void>((resolve) => {
    resolvePromise = resolve;
  });

  function checkIfComplete() {
    if (pendingImages.size === 0) {
      resolvePromise();
    }
  }

  // // Initial scan of existing elements
  processElement(rootElement);

  // // Set up MutationObserver for dynamic content
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          processElement(element);
        }
      });
    });
  });

  observer.observe(rootElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'style', 'class'],
  });

  // // Clean up function
  const cleanup = () => {
    observer.disconnect();
    abortController.abort();
  };

  // // If no images to track initially, resolve immediately
  if (pendingImages.size === 0) {
    cleanup();
    return Promise.resolve();
  }

  // // Set up timeout
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => {
      cleanup();
      resolve();
    }, maxWaitTime);
  });

  try {
    await Promise.race([imageLoadPromise, timeoutPromise]);
  } finally {
    cleanup();
  }
}

/**
 * Main function that waits for both fonts and images
 */
export async function waitForAssetsToLoad(
  maxWaitTime: number = DEFAULT_MAX_WAIT_TIME,
  rootElement: Element = document.body,
): Promise<void> {
  await Promise.all([waitForFonts(maxWaitTime), waitForImages(maxWaitTime, rootElement)]);
}

/**
 * Preloads a list of font-family names directly, without scanning the DOM for elements that
 * currently use them. `waitForFonts` above only catches fonts already needed by elements that
 * exist *right now* — it's blind to a font an upcoming stage/view is about to introduce for the
 * first time, which is exactly the "FOIT on first use" pop-in this is meant to prevent. Pass the
 * family names an upcoming stage will need before revealing it, mirroring `preloadImageUrls`.
 * Already-loaded families resolve immediately, so it's safe to call with the same fixed list on
 * every transition rather than tracking which stage introduces which family.
 */
export async function preloadFontFamilies(
  families: string[],
  maxWaitTime: number = DEFAULT_MAX_WAIT_TIME,
): Promise<void> {
  const fontsToBeLoaded = Array.from(document.fonts).filter(
    (fontFace) =>
      families.includes(fontFace.family) &&
      fontFace.status !== 'loaded' &&
      fontFace.status !== 'error',
  );
  if (!fontsToBeLoaded.length) return;

  const loadPromise = Promise.all(
    fontsToBeLoaded.map((fontFace) => fontFace.load().catch(() => fontFace)),
  ).then(() => undefined);

  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(resolve, maxWaitTime);
  });

  await Promise.race([loadPromise, timeoutPromise]);
}

/**
 * Preloads a list of image URLs directly, without scanning the DOM for existing <img> elements.
 * Resolves once every image has either loaded or failed, or after maxWaitTime — whichever comes
 * first (a slow/broken image never blocks forever). Useful for warming assets an upcoming stage
 * of a multi-stage overlay will need *before* that stage is revealed, rather than discovering them
 * only after they've already mounted and started popping in one by one.
 */
export async function preloadImageUrls(
  urls: (string | null | undefined)[],
  maxWaitTime: number = DEFAULT_MAX_WAIT_TIME,
): Promise<void> {
  const uniqueUrls = Array.from(new Set(urls.filter((url): url is string => Boolean(url))));
  if (!uniqueUrls.length) return;

  const loadPromise = Promise.all(
    uniqueUrls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
          img.src = url;
        }),
    ),
  ).then(() => undefined);

  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(resolve, maxWaitTime);
  });

  await Promise.race([loadPromise, timeoutPromise]);
}
